import React, { useState, useEffect, useCallback } from 'react';
import MainSidebar from 'components/MainSidebar';
import Icon from 'components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const TIER_COLORS = {
  'Bronze I':   { bg: 'bg-amber-900/30',   border: 'border-amber-700',   text: 'text-amber-400',   icon: '🥉' },
  'Bronze II':  { bg: 'bg-amber-900/30',   border: 'border-amber-600',   text: 'text-amber-300',   icon: '🥉' },
  'Silver I':   { bg: 'bg-slate-700/30',   border: 'border-slate-400',   text: 'text-slate-300',   icon: '🥈' },
  'Silver II':  { bg: 'bg-slate-700/30',   border: 'border-slate-300',   text: 'text-slate-200',   icon: '🥈' },
  'Gold I':     { bg: 'bg-yellow-900/30',  border: 'border-yellow-500',  text: 'text-yellow-400',  icon: '🥇' },
  'Gold II':    { bg: 'bg-yellow-900/30',  border: 'border-yellow-400',  text: 'text-yellow-300',  icon: '🥇' },
  'Platinum I': { bg: 'bg-cyan-900/30',    border: 'border-cyan-500',    text: 'text-cyan-400',    icon: '💎' },
  'Platinum II':{ bg: 'bg-cyan-900/30',    border: 'border-cyan-400',    text: 'text-cyan-300',    icon: '💎' },
  'Diamond':    { bg: 'bg-blue-900/30',    border: 'border-blue-400',    text: 'text-blue-300',    icon: '💠' },
  'Legend':     { bg: 'bg-purple-900/30',  border: 'border-purple-400',  text: 'text-purple-300',  icon: '👑' },
  'Weekly':     { bg: 'bg-green-900/30',   border: 'border-green-500',   text: 'text-green-400',   icon: '🎁' },
};

const TIER_XP = [0, 5000, 15000, 30000, 50000, 75000, 100000, 150000, 250000, 500000];

function xpForNextTier(currentTier) {
  return TIER_XP?.[currentTier] ?? null;
}

function xpProgress(currentXp, currentTier) {
  const tierStart = TIER_XP?.[currentTier - 1] ?? 0;
  const tierEnd   = TIER_XP?.[currentTier]     ?? TIER_XP?.[TIER_XP?.length - 1];
  if (tierEnd === tierStart) return 100;
  return Math.min(100, Math.round(((currentXp - tierStart) / (tierEnd - tierStart)) * 100));
}

export default function SeasonPassPage() {
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [claimingWeekly, setClaimingWeekly] = useState(false);
  const [claimResult, setClaimResult] = useState(null);
  const [activeTab, setActiveTab] = useState('tiers');

  const fetchData = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const { data: result, error: rpcError } = await supabase
        ?.rpc('get_season_pass_data', { p_user_id: user?.id });
      if (rpcError) throw rpcError;
      if (result?.success === false) throw new Error(result.error);
      setData(result);
    } catch (err) {
      setError(err?.message || 'Failed to load season pass data');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function handleClaimWeeklyBonus() {
    if (!data?.season?.id || !user?.id) return;
    setClaimingWeekly(true);
    setClaimResult(null);
    try {
      const { data: result, error: rpcError } = await supabase
        ?.rpc('claim_weekly_bonus', { p_user_id: user?.id, p_season_id: data?.season?.id });
      if (rpcError) throw rpcError;
      setClaimResult(result);
      if (result?.success) await fetchData();
    } catch (err) {
      setClaimResult({ success: false, error: err?.message });
    } finally {
      setClaimingWeekly(false);
    }
  }

  const progress = data?.progress;
  const season   = data?.season;
  const tiers    = data?.tiers || [];
  const currentWeek = data?.current_week || 1;

  const mainTiers   = tiers?.filter(t => t?.unlock_type !== 'WEEKLY_BONUS');
  const weeklyTiers = tiers?.filter(t => t?.unlock_type === 'WEEKLY_BONUS');

  const weeklyAlreadyClaimed = progress?.last_weekly_bonus_week >= currentWeek;

  return (
    <div className="flex h-screen bg-gray-950 text-white overflow-hidden">
      <MainSidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(p => !p)}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 bg-gray-900">
          <div className="flex items-center gap-3">
            <Icon name="Star" size={22} className="text-yellow-400" />
            <div>
              <h1 className="text-lg font-bold text-white">
                {season?.name || 'Season Pass'}
              </h1>
              {season && (
                <p className="text-xs text-gray-400">
                  Season {season?.season_number} &bull; Ends {new Date(season.end_date)?.toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={fetchData}
            className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
          >
            <Icon name="RefreshCw" size={16} className="text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {loading && (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-400" />
            </div>
          )}

          {error && !loading && (
            <div className="bg-red-900/30 border border-red-700 rounded-xl p-4 text-red-300">
              <Icon name="AlertCircle" size={16} className="inline mr-2" />
              {error}
            </div>
          )}

          {!loading && !error && data && (
            <>
              {/* Progress Overview */}
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-gray-400">Current Tier</p>
                    <p className="text-2xl font-bold text-white">
                      Tier {progress?.current_tier ?? 1}
                      <span className="ml-2 text-base font-normal text-gray-400">
                        {mainTiers?.find(t => t?.tier_level === progress?.current_tier)?.tier_label || 'Bronze I'}
                      </span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-400">Season XP</p>
                    <p className="text-2xl font-bold text-yellow-400">
                      {(progress?.total_season_xp ?? 0)?.toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* XP Progress Bar */}
                <div className="mb-2">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Tier {progress?.current_tier ?? 1}</span>
                    <span>
                      {progress?.current_tier < 10
                        ? `${xpForNextTier(progress?.current_tier ?? 1)?.toLocaleString()} XP to Tier ${(progress?.current_tier ?? 1) + 1}`
                        : 'MAX TIER'}
                    </span>
                  </div>
                  <div className="w-full bg-gray-800 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-yellow-500 to-yellow-300 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${xpProgress(progress?.total_season_xp ?? 0, progress?.current_tier ?? 1)}%` }}
                    />
                  </div>
                </div>

                {/* Weekly Bonus Multiplier */}
                <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-800">
                  <div className="flex items-center gap-2">
                    <Icon name="Zap" size={16} className="text-green-400" />
                    <span className="text-sm text-gray-300">
                      Weekly Bonus Multiplier:
                      <span className="ml-1 font-bold text-green-400">
                        {progress?.weekly_bonus_multiplier ?? 1.0}x
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Calendar" size={16} className="text-blue-400" />
                    <span className="text-sm text-gray-300">
                      Week <span className="font-bold text-blue-400">{currentWeek}</span> / 10
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Gift" size={16} className="text-purple-400" />
                    <span className="text-sm text-gray-300">
                      Cosmetics Unlocked:
                      <span className="ml-1 font-bold text-purple-400">
                        {progress?.unlocked_cosmetics_count ?? 0}
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Weekly Bonus Claim */}
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-white flex items-center gap-2">
                      <Icon name="Gift" size={18} className="text-green-400" />
                      Weekly Bonus — Week {currentWeek}
                    </h3>
                    <p className="text-sm text-gray-400 mt-1">
                      {weeklyAlreadyClaimed
                        ? `Claimed! Come back next week for Week ${currentWeek + 1}.`
                        : `Claim your Week ${currentWeek} bonus to increase your XP multiplier to ${Math.min(2.0, (progress?.weekly_bonus_multiplier ?? 1.0) + 0.1)?.toFixed(1)}x!`}
                    </p>
                  </div>
                  <button
                    onClick={handleClaimWeeklyBonus}
                    disabled={weeklyAlreadyClaimed || claimingWeekly}
                    className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                      weeklyAlreadyClaimed
                        ? 'bg-gray-700 text-gray-500 cursor-not-allowed' :'bg-green-600 hover:bg-green-500 text-white'
                    }`}
                  >
                    {claimingWeekly ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Claiming...
                      </span>
                    ) : weeklyAlreadyClaimed ? (
                      <span className="flex items-center gap-2">
                        <Icon name="CheckCircle" size={16} /> Claimed
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Icon name="Gift" size={16} /> Claim Bonus
                      </span>
                    )}
                  </button>
                </div>

                {claimResult && (
                  <div className={`mt-3 p-3 rounded-lg text-sm ${
                    claimResult?.success
                      ? 'bg-green-900/30 border border-green-700 text-green-300' :'bg-red-900/30 border border-red-700 text-red-300'
                  }`}>
                    {claimResult?.success
                      ? `✅ Bonus claimed! New multiplier: ${claimResult?.new_multiplier}x`
                      : `❌ ${claimResult?.error}`}
                  </div>
                )}
              </div>

              {/* Tabs */}
              <div className="flex gap-2 border-b border-gray-800">
                {['tiers', 'weekly']?.map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 text-sm font-medium capitalize transition-colors border-b-2 ${
                      activeTab === tab
                        ? 'border-yellow-400 text-yellow-400' :'border-transparent text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    {tab === 'tiers' ? 'Season Tiers' : 'Weekly Bonuses'}
                  </button>
                ))}
              </div>

              {/* Tier Grid */}
              {activeTab === 'tiers' && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {mainTiers?.map(tier => {
                    const colors = TIER_COLORS?.[tier?.tier_label] || TIER_COLORS?.['Bronze I'];
                    const isUnlocked = tier?.is_unlocked;
                    const isCurrent = tier?.tier_level === progress?.current_tier;

                    return (
                      <div
                        key={tier?.tier_level}
                        className={`relative rounded-xl border p-4 transition-all ${
                          isUnlocked
                            ? `${colors?.bg} ${colors?.border}`
                            : 'bg-gray-900/50 border-gray-800 opacity-60'
                        } ${isCurrent ? 'ring-2 ring-yellow-400' : ''}`}
                      >
                        {isCurrent && (
                          <span className="absolute -top-2 left-3 bg-yellow-400 text-gray-900 text-xs font-bold px-2 py-0.5 rounded-full">
                            CURRENT
                          </span>
                        )}
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">{colors?.icon}</span>
                            <div>
                              <p className={`font-bold text-sm ${colors?.text}`}>{tier?.tier_label}</p>
                              <p className="text-xs text-gray-500">Tier {tier?.tier_level}</p>
                            </div>
                          </div>
                          {isUnlocked ? (
                            <Icon name="CheckCircle" size={18} className="text-green-400" />
                          ) : (
                            <Icon name="Lock" size={18} className="text-gray-600" />
                          )}
                        </div>
                        <p className="text-sm font-semibold text-white mb-1">{tier?.cosmetic_name}</p>
                        <p className="text-xs text-gray-400 mb-3">{tier?.description}</p>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">
                            <Icon name="Zap" size={12} className="inline mr-1" />
                            {tier?.xp_required?.toLocaleString()} XP
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            tier?.cosmetic_rarity === 'legendary' ? 'bg-purple-900/50 text-purple-300' :
                            tier?.cosmetic_rarity === 'epic'      ? 'bg-blue-900/50 text-blue-300' :
                            tier?.cosmetic_rarity === 'rare'      ? 'bg-cyan-900/50 text-cyan-300' :
                            tier?.cosmetic_rarity === 'uncommon'? 'bg-green-900/50 text-green-300' : 'bg-gray-800 text-gray-400'
                          }`}>
                            {tier?.cosmetic_rarity}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Weekly Bonuses */}
              {activeTab === 'weekly' && (
                <div className="space-y-3">
                  {weeklyTiers?.length === 0 && (
                    <p className="text-gray-500 text-sm text-center py-8">No weekly bonuses available yet.</p>
                  )}
                  {weeklyTiers?.map(tier => {
                    const isClaimed = progress?.last_weekly_bonus_week >= (tier?.week_number || 0);
                    const isCurrentWeek = tier?.week_number === currentWeek;
                    return (
                      <div
                        key={`weekly-${tier?.week_number}`}
                        className={`flex items-center justify-between p-4 rounded-xl border ${
                          isClaimed
                            ? 'bg-green-900/20 border-green-800'
                            : isCurrentWeek
                              ? 'bg-yellow-900/20 border-yellow-700' :'bg-gray-900 border-gray-800 opacity-60'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xl">🎁</span>
                          <div>
                            <p className="font-semibold text-sm text-white">
                              Week {tier?.week_number}: {tier?.cosmetic_name}
                            </p>
                            <p className="text-xs text-gray-400">{tier?.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            tier?.cosmetic_rarity === 'legendary' ? 'bg-purple-900/50 text-purple-300' :
                            tier?.cosmetic_rarity === 'epic'      ? 'bg-blue-900/50 text-blue-300' :
                            tier?.cosmetic_rarity === 'rare'? 'bg-cyan-900/50 text-cyan-300' : 'bg-gray-800 text-gray-400'
                          }`}>
                            {tier?.cosmetic_rarity}
                          </span>
                          {isClaimed ? (
                            <Icon name="CheckCircle" size={18} className="text-green-400" />
                          ) : isCurrentWeek ? (
                            <Icon name="Gift" size={18} className="text-yellow-400" />
                          ) : (
                            <Icon name="Lock" size={18} className="text-gray-600" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
